<template>
  <div>
    <b-loading :active.sync="isLoading" :is-full-page="true"></b-loading>

    <div>
      <div class="title">Step 1: choose your options</div>
      <b-field horizontal label="Select types:" label-position="on-border">
        <div v-for="(typAct, index) in activityTypesArray">
          <b-checkbox-button
              :key="index"
              v-model="checkboxActivityType"
              :expanded="false"
              :native-value="typAct"
              style="padding-right: 4px"
              type="is-info"
              @input="addActivities"
          >
            <b-icon icon="check"></b-icon>
            <span>{{ typAct }}</span>
          </b-checkbox-button>
          <b-slider v-model="slidersActivityTypes[index]"
                    :disabled="isSliderDisabled(index)"
                    :max="getMaxForSlider(index)"
                    :min="getMinForSlider(index)"
                    :tooltip-always="true"
                    style="padding-left: 5px; padding-right: 5px"
                    type="is-info"
                    @change="addActivities"
          >
            <template>
              <b-slider-tick :value="getMinForSlider(index)">{{ getMinForSlider(index) }}</b-slider-tick>
              <b-slider-tick :value="getMaxForSlider(index)">{{ getMaxForSlider(index) }}</b-slider-tick>
            </template>
          </b-slider>
        </div>
      </b-field>
      <b-field horizontal label="Total activities required:" label-position="on-border">
        <b-input v-model="nbOfActivities" :disabled="true"></b-input>
      </b-field>
      <b-field horizontal label="Include words:" label-position="on-border">
        <b-input v-model="wordsFilter" icon="magnify"></b-input>
      </b-field>
      <b-field style="text-align: center; margin-top: 60px; margin-bottom: 40px">
        <b-button class="btn-margin"
                  icon-left="magnify"
                  type="is-success"
                  @click="viewRandomActivities">
          View the matching activities
        </b-button>
      </b-field>
    </div>

    <div v-if="step2">
      <div class="title">Step 2: Overview of matching activities</div>

      <b-message v-if="filterRawActivities.length < nbOfActivities" type="is-warning">
        <strong>Some of your filter conditions could not be applied</strong>. Simplify your search by selecting fewer or
        different types of activities, trying other search terms etc.
      </b-message>

      <raw-activities-random-table :random-raw-activities="filterRawActivities" :shuffle-display="false"
                                   :detailed="true"
                                   max-lines-selected="100">
      </raw-activities-random-table>

      <b-field style="text-align: center; margin-top: 60px; margin-bottom: 40px">
        <b-button class="btn-margin"
                  icon-left="check"
                  type="is-success"
                  :disabled="filterRawActivities.length === 0"
                  @click="finalisePackInfo">
          Finalise the pack information
        </b-button>
      </b-field>

    </div>
    <div v-if="step3">
      <div class="title">Step 3: Pack information</div>

      <b-field horizontal label="Name" label-position="on-border">
        <b-input v-model="packName" :expanded="false" placeholder="Pack name"></b-input>
      </b-field>

      <b-field horizontal label="Description" label-position="on-border">
        <b-input v-model="packDescription" :expanded="false" placeholder="Pack description"></b-input>
      </b-field>

      <b-field horizontal label="Suitable for:" label-position="on-border">
        <b-field>
          <b-checkbox-button v-for="(age, index) in ages"
                             :key="index"
                             v-model="suitableFor"
                             :native-value="age"
                             style="padding-right: 4px"
                             type="is-info">
            <b-icon icon="check"></b-icon>
            <span>{{ age }}</span>
          </b-checkbox-button>
        </b-field>
      </b-field>

      <b-field horizontal label="Tags:" label-position="on-border">
        <b-taginput
            v-model="packTags"
            :allow-new="true"
            ellipsis
            icon="label"
            placeholder="Add a tag"
            type="is-warning">
        </b-taginput>
      </b-field>

      <b-field>
        <b-button class="btn-margin" icon-left="check" type="is-success" @click="goCreate">Create
          the pack with N activities
        </b-button>
      </b-field>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import displayHelper from '../../helpers/display-helper'
import packService from '@/services/packService'
import activityTypesHelper from '../../enums/activityTypes'
import rawActivityService from '@/services/rawActivityService'
import RawActivitiesRandomTable from '@/components/single/RawActivitiesRandomTable'
import PACK_SUITABLE_FOR_STATUS from '@/enums/packSuitableForStatus'
import dateHelper from '@/helpers/date-helper'

export default {
  name: 'generate-random-packs',
  components: {RawActivitiesRandomTable},
  created() {
    this.activityTypes = activityTypesHelper.ACTIVITY_TYPES
    this.activityTypesArray = _.filter(Object.values(activityTypesHelper.ACTIVITY_TYPES), (type) => {
      return !activityTypesHelper.disabledActivities(type)
    }).sort()
    this.checkboxActivityType = this.activityTypesArray

    this.ages = PACK_SUITABLE_FOR_STATUS
    this.packTags = ['random']

    this.loadRawActivitiesList()
        .then(() => {
          for (let i = 0; i < this.activityTypesArray.length; i++) {
            let val = _.countBy(this.rawActivities, (activity) => {
              return activity.activityType === this.activityTypesArray[i]
            })

            this.maxAndMinActivities[i] = {
              min: val.true > 0 ? 1 : 0,
              max: val.true
            }
            this.slidersActivityTypes[i] = 1
          }
          this.addActivities()
        })
  },
  data() {
    return {
      isLoading: false,
      activityTypes: [],
      checkboxActivityType: [],
      activityTypesArray: [],
      nbOfActivities: 0,
      slidersActivityTypes: [],
      wordsFilter: '',

      rawActivities: [],
      rawActivitiesPerType: [],
      filterRawActivities: [],
      maxAndMinActivities: [],

      packName: '',
      packDescription: '',
      suitableFor: [],
      packTags: [],
      ages: [],

      step2: false,
      step3: false,

      columns: [
        {field: 'name'},
        {field: 'question'},
        {field: 'tags'},
      ],
    }
  },
  methods: {
    addActivities() {
      let sum = 0
      for (let i = 0; i < this.activityTypesArray.length; i++) {
        if (!this.isSliderDisabled(i)) {
          sum += this.slidersActivityTypes[i]
        }
      }
      this.nbOfActivities = sum
    },
    isSliderDisabled(index) {
      return !this.checkboxActivityType.includes(this.activityTypesArray[index])
    },
    getMaxForSlider(index) {
      return this.maxAndMinActivities[index] ? this.maxAndMinActivities[index].max : 1
    },
    getMinForSlider(index) {
      return this.maxAndMinActivities[index] ? this.maxAndMinActivities[index].min : 1
    },
    viewRandomActivities() {
      this.step2 = true
      this.filterRawActivities = []
      let filterActivitiesOnWords = this.rawActivities

      if (this.wordsFilter && this.wordsFilter !== '') {
        let searchLower = this.wordsFilter.toLowerCase().trim()
        filterActivitiesOnWords = _.filter(this.rawActivities, r => {
          const rowContent = r.content
          let found = false
          for (let col of this.columns) {
            const theRow = col.field === 'question' ? rowContent.question : r[col.field]
            if (theRow) {
              found = theRow.toString().toLowerCase().indexOf(searchLower) >= 0
              if (found) {
                return true
              }
            }
          }
          return found
        })
      }

      for (let i = 0; i < this.activityTypesArray.length; i++) {
        let activitiesArray = _.filter(filterActivitiesOnWords, (activity) => {
          return activity.activityType === this.activityTypesArray[i] &&
              !this.isSliderDisabled(i)
        })

        this.filterRawActivities = _.concat(this.filterRawActivities,
            _.slice(_.shuffle(activitiesArray), 0, this.slidersActivityTypes[i]))
      }
    },
    finalisePackInfo() {
      this.packDescription = 'Generated randomly with ' + _.join(this.checkboxActivityType, ', ') + ' activity types'
      this.step3 = true
    },
    loadRawActivitiesList() {
      this.isLoading = true
      const parent = this
      return rawActivityService.getRawActivities()
          .then((rawActivities) => {
            this.rawActivities = rawActivities
          })
          .catch((err) => {
            displayHelper.displayError(parent, 'Oops.. An error occurred: ' + err.message)
          })
          .finally(() => {
            this.isLoading = false
          })
    },
    goCreate() {
      this.isLoading = true
      console.log('new pack')
      const pack = {
        name: this.packName,
        description: this.packDescription,
        createdDate: dateHelper.getFormattedStringDateNow(),
        suitableFor: this.suitableFor,
        tags: this.packTags
      }

      let rawActivityIdsArray = []
      this.filterRawActivities.forEach(selectedRawActivity => {
        rawActivityIdsArray.push(selectedRawActivity.rawActivityId)
      })

      pack.rawActivityIds = rawActivityIdsArray

      this.isLoading = true

      return packService.createPack(pack)
          .then((creationPackResult) => {
            if (creationPackResult.isSuccess) {
              displayHelper.displaySuccess(this, 'Pack has successfully been created')
            } else {
              displayHelper.displayError(this, 'Oops, there was an error: ' + creationPackResult.errors)
            }
            this.isLoading = false
            this.$router.push({name: 'Packs'})
          })
    },
  }
}
</script>

<style scoped>
.title {
  margin-top: 20px;
}
</style>
